import { useEffect, useState, useRef, useCallback } from 'react'
import { useChartStore } from '../store/chartStore'

interface UseWebSocketResult {
  lastPrice: number | null
  isConnected: boolean
  connect: () => void
  disconnect: () => void
}

// WebSocket hook for real-time price updates
// This is a mock implementation - replace with real WebSocket connection
export function useWebSocket(symbol: string): UseWebSocketResult {
  const [lastPrice, setLastPrice] = useState<number | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const intervalRef = useRef<number | null>(null)
  const { setWsConnected, currentPrice } = useChartStore()

  // Mock WebSocket - simulates real-time price updates
  const connect = useCallback(() => {
    // In production, connect to real WebSocket:
    // wsRef.current = new WebSocket(`wss://stream.binance.com:9443/ws/${symbol.toLowerCase().replace('/', '')}@trade`)
    
    // For demo, simulate with interval
    setIsConnected(true)
    setWsConnected(true)
    
    let basePrice = currentPrice || 67000
    
    intervalRef.current = window.setInterval(() => {
      // Simulate price tick
      const change = (Math.random() - 0.5) * 0.001
      basePrice = basePrice * (1 + change)
      setLastPrice(basePrice)
    }, 1000 + Math.random() * 500)
    
    console.log(`[WebSocket] Connected to ${symbol}`)
  }, [symbol, currentPrice, setWsConnected])

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setIsConnected(false)
    setWsConnected(false)
    console.log(`[WebSocket] Disconnected from ${symbol}`)
  }, [symbol, setWsConnected])

  // Connect on mount, reconnect on symbol change
  useEffect(() => {
    connect()
    return () => disconnect()
  }, [symbol]) // Only reconnect when symbol changes

  // Real WebSocket implementation example (commented out):
  /*
  const connectReal = useCallback(() => {
    const wsUrl = getWebSocketUrl(symbol)
    wsRef.current = new WebSocket(wsUrl)

    wsRef.current.onopen = () => {
      setIsConnected(true)
      setWsConnected(true)
      console.log(`[WebSocket] Connected to ${symbol}`)
    }

    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        // Binance format: { p: "price", q: "quantity", T: timestamp }
        const price = parseFloat(data.p)
        if (!isNaN(price)) {
          setLastPrice(price)
        }
      } catch (e) {
        console.error('[WebSocket] Parse error:', e)
      }
    }

    wsRef.current.onerror = (error) => {
      console.error('[WebSocket] Error:', error)
    }

    wsRef.current.onclose = () => {
      setIsConnected(false)
      setWsConnected(false)
      // Auto-reconnect after 5 seconds
      setTimeout(connect, 5000)
    }
  }, [symbol, setWsConnected])
  */

  return {
    lastPrice,
    isConnected,
    connect,
    disconnect,
  }
}

// Helper to get WebSocket URL for different exchanges
function getWebSocketUrl(symbol: string): string {
  const formattedSymbol = symbol.toLowerCase().replace('/', '')
  
  // Add more exchanges as needed
  if (symbol.includes('USDT')) {
    // Binance
    return `wss://stream.binance.com:9443/ws/${formattedSymbol}@trade`
  } else if (symbol.includes('USD') && symbol.length === 7) {
    // Forex - would need a forex data provider
    return `wss://forex-stream.example.com/ws/${formattedSymbol}`
  }
  
  // Default to Binance
  return `wss://stream.binance.com:9443/ws/${formattedSymbol}@trade`
}

// Custom hook for subscribing to multiple symbols
export function useMultiSymbolWebSocket(symbols: string[]): Map<string, number> {
  const [prices, setPrices] = useState<Map<string, number>>(new Map())
  
  useEffect(() => {
    // In production, open one WebSocket connection for all symbols
    // For demo, just use intervals
    const intervals: number[] = []
    
    symbols.forEach(symbol => {
      const basePrice = getBasePrice(symbol)
      let price = basePrice
      
      const intervalId = window.setInterval(() => {
        price = price * (1 + (Math.random() - 0.5) * 0.001)
        setPrices(prev => new Map(prev).set(symbol, price))
      }, 1000 + Math.random() * 500)
      
      intervals.push(intervalId)
    })
    
    return () => intervals.forEach(id => clearInterval(id))
  }, [symbols.join(',')])
  
  return prices
}

function getBasePrice(symbol: string): number {
  const basePrices: Record<string, number> = {
    'BTC/USDT': 67000,
    'ETH/USDT': 3500,
    'SOL/USDT': 140,
    'EUR/USD': 1.085,
    'XAUUSD': 2340,
  }
  return basePrices[symbol] || 100
}
